import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, PaperPlaneTilt } from "@phosphor-icons/react";

export default function TransportOrderModal({ open, onClose, ride, endpoint }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  // Phase 2C — `endpoint` is optional. Default behavior (S.DRIVE dispatch
  // assigned ride) is unchanged; personal-ride callers pass an explicit
  // `/driver/personal-rides/{id}/transport-order` so the same modal renders
  // a driver-branded transport order without S.DRIVE identity.
  useEffect(() => {
    if (open && ride?.id) {
      const url = endpoint || `/rides/${ride.id}/transport-order`;
      setLoading(true);
      api.get(url)
        .then(r => setText(r.data.text))
        .catch(() => toast.error("Failed to generate transport order"))
        .finally(() => setLoading(false));
    }
  }, [open, ride, endpoint]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Copied to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transport Order</DialogTitle>
          <DialogDescription className="text-zinc-500">{ride?.route_label}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <pre className="bg-[#0a0a0a] border border-zinc-800 rounded-lg p-4 text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed max-h-[60vh] overflow-y-auto">{text}</pre>
            <div className="flex gap-2">
              <Button data-testid="copy-transport-order" onClick={copyToClipboard} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10">
                <Copy size={16} className="mr-1.5" /> Copy
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
